import { ChevronLeft, ChevronRight } from 'assets/icons';
import Button from 'components/button';
import CompactEmptyState from 'components/EmptyState/CompactEmptyState';
import IconTooltip from 'components/iconTooltip/IconTooltip';
import PageForm from 'components/pageForm/PageForm';
import { apiErrorText } from 'helper/apiErrorText';
import AgencyGoalsCard from 'components/stats/AgencyGoalsCard';
import FeelingCard from 'components/stats/FeelingCard';
import PeriodBanner from 'components/stats/PeriodBanner';
import PeriodPicker from 'components/stats/PeriodPicker';
import ProspectionCard from 'components/stats/ProspectionCard';
import ResultsCard from 'components/stats/ResultsCard';
import { REVIEW_PANEL, STATS_REVIEW_SAVED } from 'components/stats/ReviewPanel';
import ReviewReminder from 'components/stats/ReviewReminder';
import StatsSkeleton from 'components/stats/StatsSkeleton';
import {
  GOAL_METRICS,
  goalOfPeople,
  goalOverPeriod,
} from 'components/stats/statsGoals';
import {
  anchorOf,
  daysOf,
  makePeriod,
  makeRange,
  mondayOf,
  periodNames,
  shiftPeriod,
} from 'components/stats/statsPeriods';
import TrendCard from 'components/stats/TrendCard';
import PillTabs from 'components/tabs/PillTabs';
import PeopleSelect from 'components/tasks/PeopleSelect';
import { commercialsOf } from 'components/companies/companyAssignees';
import { seesTeamStats } from 'helper/roles';
import { addDays, fromIsoDay, isoDay } from 'helper/timeLabels';
import { useOnChange } from 'hooks/useChangeSignal';
import UseContext from 'hooks/useContext';
import useCrmContext from 'hooks/useCrmContext';
import Usefetchdata from 'hooks/usefetchdata';
import useText from 'hooks/useText';
import { weekdayName } from 'pages/planning/planningItems';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import crmApi from 'server/crm/crmApi';
import {
  bannerOf,
  chartOf,
  feelingOf,
  goalOf,
  kindOfSlug,
  periodTotal,
  rangeOf,
  remindsReview,
  slugOfKind,
  weekTotal,
} from './statsItems';

// Whose figures the agency manager looks at, kept per manager in this
// browser: 'team' or the ids of some of those who prospect.
const peopleKey = (userId) => `stats-people:${userId}`;
const readPeople = (key) => {
  try {
    return JSON.parse(localStorage.getItem(key)) ?? 'team';
  } catch {
    return 'team';
  }
};
const storePeople = (key, value) => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Storage unavailable: the choice lasts as long as the page.
  }
};
const fullName = (person) =>
  [person?.firstname, person?.lastname].filter(Boolean).join(' ');

/**
 * « Statistiques » of the CRM prototype: the prospecting of a week, a month,
 * a quarter or a period picked freely (`?vue=`, `?au=`, `?du=`), against the
 * period before: the banner of the goal, the funnel, the results, the chart
 * and « Mon ressenti », with the weekly review. A commercial (510) sees
 * theirs; the agency manager (500) those of the team (the 510 and the 500,
 * who prospect too), picked in the list of the bar, and reads their reviews;
 * picking himself alone, he sees his own as a commercial does. The figures come from
 * the api (admin/crm/stats/*, contract in server/crm/crmApi.js).
 */
const StatsPage = () => {
  const { text, plural, language } = useText('stats');
  const { call, ready, me } = useCrmContext();
  const { userData } = Usefetchdata();
  const { setObjID, setSlideOverType, setSlideOverShow } = UseContext();
  const [params, setParams] = useSearchParams();
  const manager = seesTeamStats(userData?.role);

  // Those who prospect, the manager himself included, for the agency manager.
  const [team, setTeam] = useState(null);
  const [chosen, setChosen] = useState(undefined);
  // The refusal of a read, null while they go through.
  const [failed, setFailed] = useState(null);
  const [goals, setGoals] = useState(null);
  const [reviews, setReviews] = useState(null);
  // The figures of each day read so far, for `key` (whose they are).
  const [days, setDays] = useState({ key: null, byDay: new Map() });
  const [metric, setMetric] = useState('rdvTaken');

  const today = new Date();
  const thisWeek = mondayOf(today);
  const kind = kindOfSlug(params.get('vue'));
  const at = params.get('au');
  const du = params.get('du');
  const period = useMemo(() => {
    if (kind !== 'custom')
      return makePeriod(kind, at ? fromIsoDay(at) : new Date());
    // Without dates, a free period is the last 30 days.
    return makeRange(
      du ? fromIsoDay(du) : addDays(new Date(), -29),
      at ? fromIsoDay(at) : new Date(),
    );
  }, [kind, at, du]);
  const previous = useMemo(() => shiftPeriod(period, -1), [period]);
  const next = useMemo(() => shiftPeriod(period, 1), [period]);
  const custom = kind === 'custom';

  const picked =
    chosen !== undefined ? chosen : me ? readPeople(peopleKey(me.id)) : 'team';
  const viewed = useMemo(() => {
    if (!manager) return me ? [me.id] : null;
    if (!team) return null;
    const ids =
      picked === 'team'
        ? []
        : picked.filter((id) => team.some((person) => person.id === id));
    return ids.length ? ids : team.map((person) => person.id);
  }, [manager, me, team, picked]);
  const viewedKey = viewed?.join(',') ?? null;
  const { from, to } = rangeOf(kind, period, previous);
  const byDay = days.key === viewedKey ? days.byDay : new Map();
  const loaded =
    days.key === viewedKey &&
    daysOf({ start: from, end: to }).every((day) => byDay.has(isoDay(day)));

  const merge = (key, rows) =>
    setDays((current) => {
      const merged = new Map(current.key === key ? current.byDay : []);
      rows.forEach((row) => merged.set(row.day, row));
      return { key, byDay: merged };
    });

  useEffect(() => {
    if (!ready || !manager) return;
    call(crmApi.getTeam).then(
      (people) => setTeam(commercialsOf(people ?? [])),
      (error) => setFailed(error ?? {}),
    );
  }, [ready, manager, call]);

  useEffect(() => {
    if (!ready) return;
    call(crmApi.getStatsGoals).then(setGoals, (error) =>
      setFailed(error ?? {}),
    );
  }, [ready, call]);

  useEffect(() => {
    if (!viewedKey || loaded || failed) return undefined;
    let alive = true;
    call(crmApi.getStatsDays, {
      from: isoDay(from),
      to: isoDay(to),
      people: viewedKey.split(','),
    }).then(
      (rows) => alive && merge(viewedKey, rows),
      (error) => alive && setFailed(error ?? {}),
    );
    return () => {
      alive = false;
    };
  }, [call, viewedKey, isoDay(from), isoDay(to), loaded, failed]);

  const loadReviews = useCallback(() => {
    if (!viewedKey) return;
    call(crmApi.getStatsReviews, { people: viewedKey.split(',') }).then(
      setReviews,
      (error) => setFailed(error ?? {}),
    );
  }, [call, viewedKey]);
  useEffect(() => {
    setReviews(null);
    loadReviews();
  }, [loadReviews]);
  // A review saved in its panel shows here at once.
  useOnChange(STATS_REVIEW_SAVED, loadReviews);

  const retry = () => {
    setFailed(null);
    if (manager && !team)
      call(crmApi.getTeam).then(
        (people) => setTeam(commercialsOf(people ?? [])),
        (error) => setFailed(error ?? {}),
      );
    if (!goals)
      call(crmApi.getStatsGoals).then(setGoals, (error) =>
        setFailed(error ?? {}),
      );
    if (!reviews) loadReviews();
  };

  const go = (nextKind, day) =>
    setParams({ vue: slugOfKind(nextKind), au: isoDay(day) });
  const goRange = (first, last) =>
    setParams({ vue: 'periode', du: isoDay(first), au: isoDay(last) });
  const ongoing = period.start <= today && today < period.end;
  const switchTo = (nextKind) => {
    if (nextKind === 'custom')
      return goRange(period.start, ongoing ? today : addDays(period.end, -1));
    if (ongoing) return go(nextKind, today);
    return go(
      nextKind,
      nextKind === 'week' ? addDays(period.end, -7) : addDays(period.start, 3),
    );
  };
  const shift = (target) =>
    custom
      ? goRange(target.start, addDays(target.end, -1))
      : go(kind, anchorOf(target));
  const pickPeople = (value) => {
    setChosen(value);
    if (me) storePeople(peopleKey(me.id), value);
  };

  const single = viewed?.length === 1;
  // The agency manager who picked himself alone reads his figures as his own.
  const own = !manager || (single && Boolean(me) && viewed[0] === me.id);
  const person =
    manager && single ? team.find((one) => one.id === viewed[0]) : null;
  const view = {
    own,
    single,
    firstName: own ? me?.firstname : person?.firstname,
  };
  const reviewed = new Set(
    single ? (reviews ?? []).map((review) => review.weekStart) : [],
  );
  const has = (monday) => reviewed.has(isoDay(monday));

  // The review of a week, read from its days (loaded first when the period
  // shown does not hold them).
  const openReview = (monday) => {
    const week = isoDay(monday);
    const show = (byDayOfWeek) => {
      setObjID({
        week,
        mode: own ? 'edit' : 'read',
        personName: fullName(person),
        totals: weekTotal(byDayOfWeek, monday),
        review:
          (reviews ?? []).find(
            (review) =>
              review.weekStart === week && review.personId === viewed[0],
          ) ?? null,
        reviewedWeeks: [...reviewed],
      });
      setSlideOverType(REVIEW_PANEL);
      setSlideOverShow(true);
    };
    const weekDays = daysOf({ start: monday, end: addDays(monday, 7) });
    if (weekDays.every((day) => byDay.has(isoDay(day)))) return show(byDay);
    return call(crmApi.getStatsDays, {
      from: week,
      to: isoDay(addDays(monday, 7)),
      people: viewed,
    }).then((rows) => {
      merge(viewedKey, rows);
      show(new Map(rows.map((row) => [row.day, row])));
    });
  };

  const names = periodNames(period, text);
  const waiting =
    !failed &&
    (!goals || (manager && !team) || (viewed && (!loaded || !reviews)));
  const noCommercial = manager && team?.length === 0;
  let content = null;
  if (failed)
    content = (
      <CompactEmptyState
        title={text('loadError')}
        description={
          apiErrorText(failed, { language, text, group: 'statsErrors' }) ||
          undefined
        }
        action={{ label: text('retry'), onClick: retry }}
      />
    );
  else if (noCommercial)
    content = (
      <CompactEmptyState
        glyph="searchX"
        title={text('noCommercialTitle')}
        description={text('noCommercialText')}
      />
    );
  else if (waiting) content = <StatsSkeleton />;
  else {
    const current = periodTotal(byDay, period);
    const before = periodTotal(byDay, previous);
    const weekGoal = goalOfPeople(goals, viewed, 'rdvTaken');
    const goal = goalOf(kind, period, weekGoal);
    // What the period aims at, metric by metric; the banner's goal for the
    // appointments booked.
    const periodGoals = Object.fromEntries(
      GOAL_METRICS.map(({ key, per }) => [
        key,
        key === 'rdvTaken'
          ? goal
          : goalOverPeriod(kind, period, goalOfPeople(goals, viewed, key), per),
      ]),
    );
    const banner = bannerOf({
      kind,
      period,
      previous,
      current,
      goal,
      weekGoal,
      byDay,
      from,
      today,
      has,
      view,
      text,
      plural,
      go,
      openReview,
    });
    const chart = chartOf({ kind, period, byDay, metric, today, text });
    const feeling = feelingOf({ kind, period, reviews, view, text });
    let feelingTitle = text('feelingTeam');
    if (single)
      feelingTitle = own
        ? text('feelingMine')
        : text('feelingOf', { name: person?.firstname ?? '' });
    const versus = text('versus', {
      period: periodNames(previous, text).short,
    });
    content = (
      <>
        <PeriodBanner {...banner} value={current.rdvTaken} goal={goal} />
        {remindsReview({ kind, period, today, has, view }) && (
          <ReviewReminder
            day={weekdayName(today)}
            onOpen={() => openReview(thisWeek)}
          />
        )}
        <ProspectionCard
          current={current}
          previous={before}
          aside={versus}
          goals={periodGoals}
        />
        <ResultsCard
          current={current}
          previous={before}
          aside={versus}
          goals={periodGoals}
        />
        {/* The agency's yearly goals, which the agency manager sets. */}
        {manager && (
          <AgencyGoalsCard year={period.year ?? period.start.getFullYear()} />
        )}
        <div className="flex flex-col items-stretch gap-6 lg:flex-row">
          <TrendCard
            title={chart.title}
            bars={chart.bars}
            metric={metric}
            onMetric={setMetric}
          />
          <FeelingCard
            title={feelingTitle}
            aside={feeling.aside}
            feeling={feeling.feeling}
            empty={feeling.empty}
          />
        </div>
      </>
    );
  }

  return (
    <PageForm>
      <div className="flex min-h-0 flex-1 flex-col">
        <div className="flex min-h-[70px] shrink-0 flex-wrap items-center gap-3 px-4 py-3 lg:px-8">
          <div className="flex items-center gap-1">
            <IconTooltip label={text('previous')}>
              <Button
                color="SECONDARY"
                size="ICON"
                className="h-10 w-10 border-gray-200"
                aria-label={text('previous')}
                onClick={() => shift(previous)}
              >
                <ChevronLeft wh={18} color="#374151" />
              </Button>
            </IconTooltip>
            <IconTooltip label={text('next')}>
              <Button
                color="SECONDARY"
                size="ICON"
                className="h-10 w-10 border-gray-200"
                aria-label={text('next')}
                disabled={next.start > today}
                onClick={() => shift(next)}
              >
                <ChevronRight wh={18} color="#374151" />
              </Button>
            </IconTooltip>
          </div>
          <PeriodPicker
            key={`${kind}-${isoDay(period.start)}`}
            period={period}
            label={names.label}
            onPick={go}
            onRange={goRange}
          />
          <span className="flex-1" />
          {/* The controls on the right sit closer together, as in the tasks' bar. */}
          <div className="flex flex-wrap items-center gap-2">
            {manager && team?.length > 0 && (
              <PeopleSelect
                team={team}
                meId={me?.id}
                value={picked}
                onChange={pickPeople}
                fallback="team"
                // Only those who prospect (510 and 500) have statistics: the list
                // holds them alone.
                allLabel="Tous les commerciaux"
              />
            )}
            <PillTabs
              activeTab={kind}
              onChange={switchTo}
              tabs={['week', 'month', 'quarter', 'custom'].map((id) => ({
                id,
                label: text(`kind_${id}`),
              }))}
            />
            {own && (
              <Button
                color="SECONDARY"
                size="SM"
                className="h-10 border-gray-200"
                onClick={() =>
                  openReview(kind === 'week' ? period.start : thisWeek)
                }
              >
                {text('myReview')}
              </Button>
            )}
          </div>
        </div>
        <main className="min-h-0 flex-1 overflow-y-auto border-t border-gray-100">
          <div className="flex flex-col gap-6 px-4 pb-6 pt-5 lg:px-8">
            {content}
          </div>
        </main>
      </div>
    </PageForm>
  );
};

export default StatsPage;
